import { memo, forwardRef, MouseEvent, useMemo } from 'react';
import { ButtonProps } from './button.types';
import { InlineSpinner } from '../inline-spinner';
import { Text } from '../text';
import {
  ContentWrapper,
  IconWrapper,
  PendingWrapper,
  StyledButton,
  StyledCounter,
} from './button.styled';

/**
 * Универсальная кнопка дизайн-системы.
 */
export const Button = memo(
  forwardRef<HTMLButtonElement, ButtonProps>(
    (
      {
        mode,
        variant = 'main',
        size = 'm',
        iconButton,
        leftIcon,
        rightIcon,
        pending,
        disabled,
        selected,
        counter,
        timer,
        children,
        type = 'button',
        ...restProps
      },
      forwardedRef
    ) => {
      const isIconOnly = !!iconButton;

      const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
        if (!pending) {
          restProps.onClick?.(e);
        }
      };

      const content = useMemo(() => {
        if (isIconOnly) {
          return <IconWrapper aria-hidden="true">{iconButton}</IconWrapper>;
        }

        return (
          <>
            {leftIcon && (
              <IconWrapper aria-hidden="true">{leftIcon}</IconWrapper>
            )}

            {children}

            {timer && (
              <Text variant="numbers-13" aria-label={`таймер: ${timer}`}>
                {timer}
              </Text>
            )}

            {!!counter && counter > 0 && (
              <StyledCounter count={counter} size="s" />
            )}

            {rightIcon && (
              <IconWrapper aria-hidden="true">{rightIcon}</IconWrapper>
            )}
          </>
        );
      }, [
        children,
        counter,
        iconButton,
        isIconOnly,
        leftIcon,
        rightIcon,
        timer,
      ]);

      return (
        <StyledButton
          ref={forwardedRef}
          type={type}
          {...restProps}
          $isIconButton={isIconOnly}
          $mode={mode}
          $variant={variant}
          $size={size}
          // ARIA атрибуты
          aria-label={
            restProps['aria-label'] ??
            (typeof children === 'string' ? children : undefined)
          }
          aria-disabled={disabled || pending}
          aria-busy={pending}
          aria-pressed={selected}
          aria-describedby={
            [timer ? 'button-timer' : null, counter ? 'button-counter' : null]
              .filter(Boolean)
              .join() || undefined
          }
          // Data атрибуты для стилизации
          data-pending={pending}
          data-selected={selected}
          disabled={disabled}
          onClick={handleClick}
        >
          {pending && (
            <PendingWrapper>
              <InlineSpinner />
              {/* Скрытый текст для скринридеров */}
              <span style={{ display: 'none' }}>Загрузка...</span>
            </PendingWrapper>
          )}

          <ContentWrapper $pending={pending}>{content}</ContentWrapper>
        </StyledButton>
      );
    }
  )
);
